import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Star, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const REVIEWS = [
  { name: "Aarav Mehta", city: "Mumbai", rating: 5, product: "Creatine Bar", text: "Finally a bar that reads like a nutrition label, not a chemistry set. The Choco Almond is dangerously good." },
  { name: "Ishita Rao", city: "Bengaluru", rating: 5, product: "Collagen Bar", text: "No bloat. I have tried every bar on the market and this is the first one my stomach actually agrees with." },
  { name: "Kabir Malhotra", city: "New Delhi", rating: 5, product: "Collagen Bar", text: "Cranberry Bliss tastes like dessert and works like a supplement. My skin is genuinely thanking me." },
  { name: "Ananya Iyer", city: "Chennai", rating: 5, product: "Creatine Bar", text: "The ashwagandha is a genius touch. Calm, steady energy through my 6am sessions." },
  { name: "Vihaan Shah", city: "Pune", rating: 4, product: "Creatine Bar", text: "The packaging alone feels like unwrapping a gift. What is inside is even better." },
  { name: "Sana Kapoor", city: "Hyderabad", rating: 5, product: "Collagen Bar", text: "Two bars a day and my protein target finally feels effortless. This is my whole routine now." },
];

const GENERATIONS = [
  {
    name: "Aarav, 17",
    tag: "Student · Footballer",
    img: "/images/review-1.jpeg",
    text: "Post practice, this is the only thing I reach for. Tastes like a treat, works like a supplement.",
  },
  {
    name: "Meera, 38",
    tag: "Marathon Runner",
    img: "/images/review-2.jpeg",
    text: "I have carried every bar on the market up a mountain. DNA is the only one my stomach never argues with.",
  },
  {
    name: "Leela, 63",
    tag: "Yoga Teacher",
    img: "/images/review-3.jpeg",
    text: "At my age, protein is non-negotiable. This is the first bar my digestion actually thanks me for.",
  },
];

const Stars = ({ n }) => (
  <div className="flex gap-1">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={13} strokeWidth={0} fill={i < n ? "#C07C5F" : "#EBE8E3"} />
    ))}
  </div>
);

const Reviews = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post("/newsletter", { email: email.trim() });
      setDone(true);
      toast.success("Welcome to the DNA family");
    } catch {
      toast.error("Could not subscribe — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reviews" className="py-32 md:py-48 border-b border-[#EBE8E3]" data-testid="reviews-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="grid md:grid-cols-12 gap-8 mb-20 md:mb-28 items-end"
        >
          <div className="md:col-span-8">
            <p className="text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F] mb-6">The Family</p>
            <h2 className="font-serif-lux font-medium text-5xl md:text-7xl tracking-tighter leading-[0.95]">
              One bar, every generation —
              <span className="italic font-light text-[#5C5C5C]"> no age limit to feeling strong.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right" data-testid="reviews-aggregate">
            <p className="font-serif-lux font-medium text-7xl leading-none">4.9</p>
            <div className="flex md:justify-end mt-3">
              <Stars n={5} />
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mt-3">
              Average early-taster rating
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-px bg-[#EBE8E3] border border-[#EBE8E3] mb-24 md:mb-32">
          {GENERATIONS.map((g, i) => (
            <motion.figure
              key={g.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
              className="bg-[#F9F8F6] group"
              data-testid={`generation-card-${i}`}
            >
              <div className="overflow-hidden aspect-[4/5]">
                <img
                  src={g.img}
                  alt={g.name}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-1000"
                />
              </div>
              <div className="p-8">
                <Stars n={5} />
                <blockquote className="font-serif-lux font-light text-xl leading-snug tracking-tight mt-4 mb-6">
                  "{g.text}"
                </blockquote>
                <figcaption className="flex items-center justify-between border-t border-[#EBE8E3] pt-4">
                  <p className="text-sm font-medium">{g.name}</p>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#C07C5F]">{g.tag}</span>
                </figcaption>
              </div>
            </motion.figure>
          ))}
        </div>

        <div className="grid md:grid-cols-3 border-t border-l border-[#EBE8E3] mb-24 md:mb-32">
          {REVIEWS.map((r, i) => (
            <motion.figure
              key={r.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease: EASE }}
              className="border-r border-b border-[#EBE8E3] p-8 md:p-10 flex flex-col gap-6 hover:bg-white transition-colors duration-700"
              data-testid={`review-card-${i}`}
            >
              <Stars n={r.rating} />
              <blockquote className="font-serif-lux font-light text-xl md:text-2xl leading-snug tracking-tight flex-1">
                "{r.text}"
              </blockquote>
              <figcaption className="flex items-center justify-between border-t border-[#EBE8E3] pt-5">
                <div>
                  <p className="text-sm font-medium">{r.name}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] mt-1">{r.city}</p>
                </div>
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#C07C5F]">{r.product}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className="grid md:grid-cols-12 gap-10 items-center border border-[#EBE8E3] bg-white px-8 md:px-14 py-14 md:py-20"
          data-testid="newsletter-block"
        >
          <div className="md:col-span-7">
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#C07C5F] mb-4">The Inner Circle</p>
            <h3 className="font-serif-lux font-medium text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              You can be part of the
              <span className="italic font-light text-[#5C5C5C]"> DNA family too.</span>
            </h3>
            <p className="mt-6 text-sm font-light text-[#5C5C5C] max-w-md leading-relaxed">
              Subscribe for launch offers, early flavour drops and quiet,
              beautiful updates. No noise — ever.
            </p>
          </div>
          <div className="md:col-span-5">
            {done ? (
              <div className="flex items-center gap-4" data-testid="newsletter-success">
                <span className="w-12 h-12 rounded-full border border-[#C07C5F] flex items-center justify-center shrink-0">
                  <Check size={18} className="text-[#C07C5F]" />
                </span>
                <p className="font-serif-lux italic font-light text-2xl">You're in. Welcome to the family.</p>
              </div>
            ) : (
              <form onSubmit={subscribe} data-testid="newsletter-form">
                <label className="block">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="newsletter-email-input"
                    placeholder="you@example.com"
                    className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-light"
                  />
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="newsletter-subscribe-btn"
                  className="group mt-8 flex items-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500 disabled:opacity-50"
                >
                  {sending ? "Joining…" : "Subscribe"}
                  <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Reviews;
